import { useRef } from "react";
import { useEditor, EditorContent, ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered,
  Quote, Minus, Link as LinkIcon, Image as ImageIcon, Table as TableIcon,
  Undo, Redo, MessageSquareQuote, Wine,
} from "lucide-react";
import { api } from "@/lib/adminApi";

/* ---------- Custom node: Pull Quote ---------- */
const PullQuote = Node.create({
  name: "pullQuote",
  group: "block",
  content: "inline*",
  defining: true,
  parseHTML() {
    return [{ tag: 'blockquote[data-type="pullquote"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["blockquote", mergeAttributes(HTMLAttributes, { "data-type": "pullquote", class: "jd-pullquote" }), 0];
  },
});

/* ---------- Custom node: Tasting Note ---------- */
function TastingNoteView({ node, updateAttributes }) {
  const { nose, palate, finish, score } = node.attrs;
  const rows = [
    ["nose", "Nose", nose],
    ["palate", "Palate", palate],
    ["finish", "Finish", finish],
  ];
  return (
    <NodeViewWrapper className="jd-tasting-edit" data-testid="tasting-note-block">
      <div contentEditable={false} className="jd-tasting-edit-inner">
        <div className="jd-tasting-edit-head">
          <span>Tasting Notes</span>
          <label className="jd-tn-score">
            Score
            <input
              type="number" min="0" max="10" step="0.1" value={score}
              placeholder="9.4" data-testid="tn-score"
              onChange={(e) => updateAttributes({ score: e.target.value })}
            />
          </label>
        </div>
        {rows.map(([key, label, val]) => (
          <label key={key} className="jd-tn-row">
            <span>{label}</span>
            <input
              value={val} placeholder={`${label} notes…`} data-testid={`tn-${key}`}
              onChange={(e) => updateAttributes({ [key]: e.target.value })}
            />
          </label>
        ))}
      </div>
    </NodeViewWrapper>
  );
}

const attr = (name) => ({
  default: "",
  parseHTML: (el) => el.getAttribute(`data-${name}`) || "",
  renderHTML: () => ({}),
});

const TastingNote = Node.create({
  name: "tastingNote",
  group: "block",
  atom: true,
  addAttributes() {
    return { nose: attr("nose"), palate: attr("palate"), finish: attr("finish"), score: attr("score") };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="tasting-note"]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    const { nose, palate, finish, score } = node.attrs;
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "tasting-note", class: "jd-tasting",
        "data-nose": nose, "data-palate": palate, "data-finish": finish, "data-score": score,
      }),
      ["div", { class: "jd-tasting-head" }, ["span", {}, "Tasting Notes"],
        ["span", { class: "jd-tasting-score" }, score ? `${score}/10` : ""]],
      ["div", { class: "jd-tasting-row" }, ["span", { class: "k" }, "Nose"], ["span", { class: "v" }, nose]],
      ["div", { class: "jd-tasting-row" }, ["span", { class: "k" }, "Palate"], ["span", { class: "v" }, palate]],
      ["div", { class: "jd-tasting-row" }, ["span", { class: "k" }, "Finish"], ["span", { class: "v" }, finish]],
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(TastingNoteView);
  },
});

/* ---------- Toolbar button ---------- */
function TB({ onClick, active, disabled, title, children, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className="jd-tb-btn"
      style={{
        background: active ? "var(--oxblood)" : "transparent",
        color: active ? "var(--bone)" : "var(--bone-dim)",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange }) {
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ HTMLAttributes: { class: "jd-content-img" } }),
      Placeholder.configure({ placeholder: "Tell the story… use the toolbar for headings, quotes, images, tables and tasting notes." }),
      Table.configure({ resizable: false, HTMLAttributes: { class: "jd-content-table" } }),
      TableRow, TableHeader, TableCell,
      PullQuote, TastingNote,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: data.url }).run();
    } catch (_) {
      window.alert("Image upload failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="jd-editor">
      <div className="jd-toolbar" data-testid="editor-toolbar">
        <TB onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testid="tb-bold"><Bold size={16} /></TB>
        <TB onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testid="tb-italic"><Italic size={16} /></TB>
        <TB onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testid="tb-strike"><Strikethrough size={16} /></TB>
        <span className="jd-tb-sep" />
        <TB onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading" testid="tb-h2"><Heading2 size={16} /></TB>
        <TB onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subheading" testid="tb-h3"><Heading3 size={16} /></TB>
        <TB onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testid="tb-ul"><List size={16} /></TB>
        <TB onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid="tb-ol"><ListOrdered size={16} /></TB>
        <TB onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testid="tb-quote"><Quote size={16} /></TB>
        <span className="jd-tb-sep" />
        <TB onClick={addLink} active={editor.isActive("link")} title="Link" testid="tb-link"><LinkIcon size={16} /></TB>
        <TB onClick={() => fileRef.current?.click()} title="Insert image" testid="tb-image"><ImageIcon size={16} /></TB>
        <TB onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table" testid="tb-table"><TableIcon size={16} /></TB>
        <TB onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider" testid="tb-divider"><Minus size={16} /></TB>
        <span className="jd-tb-sep" />
        <TB onClick={() => editor.chain().focus().toggleNode("pullQuote", "paragraph").run()} active={editor.isActive("pullQuote")} title="Pull quote" testid="tb-pullquote"><MessageSquareQuote size={16} /></TB>
        <TB onClick={() => editor.chain().focus().insertContent({ type: "tastingNote", attrs: { nose: "", palate: "", finish: "", score: "" } }).run()} title="Tasting note" testid="tb-tasting"><Wine size={16} /></TB>
        <span className="jd-tb-sep" />
        <TB onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid="tb-undo"><Undo size={16} /></TB>
        <TB onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid="tb-redo"><Redo size={16} /></TB>
      </div>

      <input ref={fileRef} type="file" accept="image/*" onChange={handleImageFile} className="hidden" data-testid="editor-image-file" />

      <EditorContent editor={editor} className="jd-editor-content" data-testid="editor-content" />
    </div>
  );
}
